import React, { useEffect, useState } from "react";
import { View, TouchableOpacity, StyleSheet, Platform } from "react-native";
// eslint-disable-next-line import/default
import BarcodeScanner from "react-native-scan-barcode";
import { check, PERMISSIONS, RESULTS, request } from "react-native-permissions";
import Text from "@components/CustomText";
import DsModal from "./DsModal";
import { tokens } from "./tokens";
import { notifyError } from "./notify";

// Crate scanner modal — re-uses react-native-scan-barcode and the
// permission flow from src/screens/MyDeliveries/SortingDeliveries/
// CrateScanner.js. On a successful decode the parent gets the crate id
// and triggers a fetch.

const requestCamera = async () => {
  try {
    const perm =
      Platform.OS === "ios"
        ? PERMISSIONS.IOS.CAMERA
        : PERMISSIONS.ANDROID.CAMERA;
    const status = await check(perm);
    if (status === RESULTS.GRANTED) return true;
    const next = await request(perm);
    return next === RESULTS.GRANTED;
  } catch {
    return false;
  }
};

const CrateScannerSheet = ({ visible, onClose, onDecode }) => {
  const [permitted, setPermitted] = useState(null);
  const [showScanner, setShowScanner] = useState(true);

  useEffect(() => {
    if (!visible) {
      setShowScanner(true);
      return;
    }
    (async () => {
      const ok = await requestCamera();
      setPermitted(ok);
      if (!ok) notifyError("Camera permission denied");
    })();
  }, [visible]);

  const handleRead = ({ data }) => {
    if (!data) return;
    setShowScanner(false);
    onDecode(String(data).trim());
  };

  return (
    <DsModal
      visible={visible}
      onClose={onClose}
      position={DsModal.POSITION.FULLSCREEN}
      contentStyle={styles.container}
    >
      <View style={styles.header}>
        <Text variant="title_16" style={styles.title}>
          Scan Crate
        </Text>
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={onClose}
          style={styles.closeBtn}
        >
          <Text variant="caption_2" style={styles.closeText}>
            Close
          </Text>
        </TouchableOpacity>
      </View>
      <View style={styles.scannerArea}>
        {permitted === false ? (
          <Text variant="caption_2" style={styles.message}>
            Grant camera permission from settings, then re-open the scanner.
          </Text>
        ) : permitted && showScanner ? (
          <BarcodeScanner onBarCodeRead={handleRead} style={styles.scanner} />
        ) : (
          <Text variant="caption_2" style={styles.message}>
            Initialising camera…
          </Text>
        )}
      </View>
      <Text variant="caption_2" style={styles.hint}>
        Point the camera at the crate&apos;s QR / barcode.
      </Text>
    </DsModal>
  );
};

export default CrateScannerSheet;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: tokens.surface },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.medium,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  title: { color: tokens.textHeading, fontWeight: "700" },
  closeBtn: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.borderMedium,
  },
  closeText: { color: tokens.textHeading, fontSize: 12, fontWeight: "600" },
  scannerArea: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.bg,
  },
  scanner: { flex: 1, width: "100%" },
  message: {
    color: tokens.textHeading,
    fontSize: 14,
    textAlign: "center",
    paddingHorizontal: tokens.spacing.xlarge,
  },
  hint: {
    color: tokens.textTertiary,
    fontSize: 11,
    textAlign: "center",
    paddingVertical: tokens.spacing.medium,
  },
});
